import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, CheckCircle, Activity, Heart, Stethoscope, Brain, TrendingUp, Bell } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ReminderModal from '@/components/ReminderModal';
import { HealthDisclaimer, EvidenceBadge } from '@/components/HealthDisclaimer';
import MensHealthHub from '@/components/MensHealthHub';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MensHealth = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Default landing is the new structured hub. A specific section drills in
  // when explicitly requested via ?section=daily|cardiovascular|screening|mental.
  const sectionParam = searchParams.get('section');

  const [activeSection, setActiveSection] = useState(sectionParam || 'hub');
  const [view, setView] = useState('input'); // input or results
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderSuggestion, setReminderSuggestion] = useState('');

  const goToSection = (key) => {
    setActiveSection(key);
    setView('input');
    setSearchParams({ section: key });
  };

  const goToHub = () => {
    setActiveSection('hub');
    setView('input');
    setSearchParams({});
  };
  
  
  // Daily Health Tracking State
  const [dailyData, setDailyData] = useState({
    stress: '',
    energy: '',
    activity: '',
    sleep: ''
  });
  
  // Cardiovascular Health State
  const [cardiovascularData, setCardiovascularData] = useState({
    systolic: '',
    diastolic: '',
    heartRate: '',
    activityLevel: '',
    smokingStatus: '',
    alcoholIntake: '',
    stressLevel: '',
    sleepQuality: ''
  });
  
  // Preventive Screening State
  const [screeningData, setScreeningData] = useState({
    age: '',
    familyHistory: ''
  });
  
  // Mental Wellness State
  const [mentalData, setMentalData] = useState({
    stressLevel: '',
    mood: '',
    sleepQuality: ''
  });
  
  const [results, setResults] = useState(null);

  // Handle Daily Health Submit
  const handleDailySubmit = (e) => {
    e.preventDefault();
    if (!dailyData.stress || !dailyData.energy || !dailyData.activity || !dailyData.sleep) {
      toast.error('Please complete all fields');
      return;
    }

    const recommendations = [];
    
    if (dailyData.stress === 'High' || dailyData.stress === 'Very High') {
      recommendations.push({
        icon: '🧘‍♂️',
        title: 'High Stress Detected',
        tip: 'Take 10-minute breaks throughout the day',
        action: 'Try deep breathing exercises or meditation'
      });
    }
    
    if (dailyData.energy === 'Low') {
      recommendations.push({
        icon: '⚡',
        title: 'Low Energy Alert',
        tip: 'Ensure 7-8 hours of sleep tonight',
        action: 'Stay hydrated and eat protein-rich meals'
      });
    }
    
    if (dailyData.activity === 'None') {
      recommendations.push({
        icon: '🚶‍♂️',
        title: 'Get Moving',
        tip: 'Start with a 10-minute walk',
        action: 'Aim for 150 minutes of moderate activity per week'
      });
    }
    
    if (dailyData.sleep === 'Poor') {
      recommendations.push({
        icon: '😴',
        title: 'Sleep Quality Concern',
        tip: 'Establish a consistent bedtime routine',
        action: 'Avoid screens 1 hour before bed, keep room cool and dark'
      });
    }

    if (recommendations.length === 0) {
      recommendations.push({
        icon: '💪',
        title: 'Great Job!',
        tip: 'Your wellness metrics look balanced',
        action: 'Keep up the good work and continue tracking'
      });
    }

    setResults({ recommendations, nextSteps: ['Track daily for best insights', 'Stay consistent with sleep schedule', 'Monitor stress triggers'] });
    setView('results');
    toast.success('Daily check-in complete! 💪');
  };

  // Handle Cardiovascular Submit
  const handleCardiovascularSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!cardiovascularData.activityLevel || !cardiovascularData.smokingStatus || 
        !cardiovascularData.alcoholIntake || !cardiovascularData.stressLevel || 
        !cardiovascularData.sleepQuality) {
      toast.error('Please complete all required fields');
      return;
    }

    const recommendations = [];
    const healthyHabits = [];
    const areasOfConcern = [];
    let riskLevel = 'Low';
    let riskScore = 0;
    
    // Blood Pressure Analysis
    let bpStatus = 'Not Provided';
    if (cardiovascularData.systolic && cardiovascularData.diastolic) {
      const sys = parseInt(cardiovascularData.systolic);
      const dia = parseInt(cardiovascularData.diastolic);
      
      if (sys >= 140 || dia >= 90) {
        bpStatus = 'High';
        riskScore += 3;
        areasOfConcern.push('⚠️ High Blood Pressure');
        recommendations.push({
          icon: '🩺',
          category: 'Blood Pressure',
          advice: 'Your blood pressure is elevated',
          action: 'Consult a healthcare provider soon. Reduce sodium intake and increase physical activity.'
        });
      } else if (sys >= 130 || dia >= 80) {
        bpStatus = 'Elevated';
        riskScore += 2;
        areasOfConcern.push('⚠️ Elevated Blood Pressure');
        recommendations.push({
          icon: '📊',
          category: 'Blood Pressure',
          advice: 'Your blood pressure is slightly elevated',
          action: 'Monitor regularly. Focus on heart-healthy diet and regular exercise.'
        });
      } else if (sys >= 90 && dia >= 60) {
        bpStatus = 'Normal';
        healthyHabits.push('✅ Healthy Blood Pressure');
        recommendations.push({
          icon: '💚',
          category: 'Blood Pressure',
          advice: 'Your blood pressure is in a healthy range',
          action: 'Maintain current lifestyle. Continue monitoring regularly.'
        });
      }
    }

    // Heart Rate Analysis
    if (cardiovascularData.heartRate) {
      const hr = parseInt(cardiovascularData.heartRate);
      if (hr > 100) {
        riskScore += 2;
        areasOfConcern.push('⚠️ Elevated Resting Heart Rate');
        recommendations.push({
          icon: '💓',
          category: 'Heart Rate',
          advice: 'Your resting heart rate is elevated',
          action: 'Regular cardio exercise can help lower resting heart rate. Consider stress management techniques.'
        });
      } else if (hr >= 60 && hr <= 100) {
        healthyHabits.push('✅ Normal Resting Heart Rate');
      } else if (hr < 60 && cardiovascularData.activityLevel !== 'High') {
        recommendations.push({
          icon: '🩺',
          category: 'Heart Rate',
          advice: 'Your resting heart rate is low',
          action: 'If not an athlete, consult your doctor about low heart rate.'
        });
      }
    }

    // Activity Level Analysis
    if (cardiovascularData.activityLevel === 'None') {
      riskScore += 3;
      areasOfConcern.push('⚠️ Insufficient Physical Activity');
      recommendations.push({
        icon: '🏃‍♂️',
        category: 'Physical Activity',
        advice: 'Regular exercise is crucial for heart health',
        action: 'Start with 10-15 minutes of walking daily. Gradually increase to 30 minutes, 5 days/week.'
      });
    } else if (cardiovascularData.activityLevel === 'Light') {
      riskScore += 1;
      recommendations.push({
        icon: '🚶‍♂️',
        category: 'Physical Activity',
        advice: 'Light activity is a good start',
        action: 'Try to increase to moderate intensity activities like brisk walking or cycling.'
      });
    } else if (cardiovascularData.activityLevel === 'Moderate' || cardiovascularData.activityLevel === 'High') {
      healthyHabits.push('✅ Regular Physical Activity');
    }

    // Smoking Status
    if (cardiovascularData.smokingStatus === 'Yes') {
      riskScore += 4;
      areasOfConcern.push('🚨 Active Smoking');
      recommendations.push({
        icon: '🚭',
        category: 'Smoking Cessation',
        advice: 'Smoking significantly increases cardiovascular risk',
        action: 'Quitting smoking is the single best thing you can do for your heart. Talk to your doctor about cessation programs.'
      });
    } else {
      healthyHabits.push('✅ Non-Smoker');
    }

    // Alcohol Intake
    if (cardiovascularData.alcoholIntake === 'Heavy (3+ drinks/day)') {
      riskScore += 3;
      areasOfConcern.push('⚠️ High Alcohol Consumption');
      recommendations.push({
        icon: '🍷',
        category: 'Alcohol Intake',
        advice: 'Heavy alcohol use increases heart disease risk',
        action: 'Reduce to moderate levels (1-2 drinks/day max). Consider speaking with a healthcare provider.'
      });
    } else if (cardiovascularData.alcoholIntake === 'Moderate (1-2 drinks/day)') {
      recommendations.push({
        icon: '✅',
        category: 'Alcohol Intake',
        advice: 'Moderate alcohol consumption',
        action: 'Current intake is within moderate limits. Continue monitoring.'
      });
    } else {
      healthyHabits.push('✅ Minimal/No Alcohol');
    }

    // Stress Level
    if (cardiovascularData.stressLevel === 'High' || cardiovascularData.stressLevel === 'Very High') {
      riskScore += 2;
      areasOfConcern.push('⚠️ High Stress Levels');
      recommendations.push({
        icon: '🧘‍♂️',
        category: 'Stress Management',
        advice: 'Chronic stress impacts heart health',
        action: 'Practice relaxation techniques: deep breathing, meditation, yoga. Consider professional support if needed.'
      });
    } else if (cardiovascularData.stressLevel === 'Low' || cardiovascularData.stressLevel === 'Moderate') {
      healthyHabits.push('✅ Managed Stress Levels');
    }

    // Sleep Quality
    if (cardiovascularData.sleepQuality === 'Poor') {
      riskScore += 2;
      areasOfConcern.push('⚠️ Poor Sleep Quality');
      recommendations.push({
        icon: '😴',
        category: 'Sleep Health',
        advice: 'Poor sleep increases cardiovascular risk',
        action: 'Aim for 7-9 hours. Establish bedtime routine, avoid screens before bed, keep room cool and dark.'
      });
    } else if (cardiovascularData.sleepQuality === 'Fair') {
      recommendations.push({
        icon: '🌙',
        category: 'Sleep Health',
        advice: 'Sleep quality could be improved',
        action: 'Focus on sleep hygiene: consistent schedule, relaxing pre-bed routine, comfortable environment.'
      });
    } else {
      healthyHabits.push('✅ Good Sleep Quality');
    }

    // Always include general heart-healthy recommendations
    recommendations.push({
      icon: '🥗',
      category: 'Heart-Healthy Diet',
      advice: 'Nutrition is key to cardiovascular health',
      action: 'Eat more fruits, vegetables, whole grains, and lean proteins. Limit saturated fats, trans fats, and sodium.'
    });

    recommendations.push({
      icon: '💧',
      category: 'Hydration',
      advice: 'Stay well-hydrated throughout the day',
      action: 'Aim for 8 glasses of water daily. Proper hydration supports heart function.'
    });

    // Calculate risk level
    if (riskScore >= 8) {
      riskLevel = 'Elevated';
    } else if (riskScore >= 4) {
      riskLevel = 'Moderate';
    } else {
      riskLevel = 'Low';
    }

    setResults({ 
      riskLevel, 
      riskScore,
      bpStatus, 
      recommendations,
      healthyHabits,
      areasOfConcern
    });
    setView('results');
    toast.success('Heart health check complete! ❤️');
  };

  // Handle Preventive Screening Submit
  const handleScreeningSubmit = (e) => {
    e.preventDefault();
    if (!screeningData.age) {
      toast.error('Please enter your age');
      return;
    }

    const age = parseInt(screeningData.age);
    const screenings = [];

    if (age >= 18) {
      screenings.push({
        test: 'Blood Pressure',
        frequency: 'Annually',
        priority: 'High'
      });
    }

    if (age >= 35) {
      screenings.push({
        test: 'Cholesterol Check',
        frequency: 'Every 5 years',
        priority: 'High'
      });
    }

    if (age >= 45) {
      screenings.push({
        test: 'Diabetes Screening (Fasting Glucose)',
        frequency: 'Every 3 years',
        priority: 'High'
      });
    }

    if (age >= 50) {
      screenings.push({
        test: 'Colorectal Cancer Screening',
        frequency: 'Every 10 years (colonoscopy) or annually (stool test)',
        priority: 'High'
      });
    }

    if (age >= 55 && cardiovascularData.smokingStatus === 'Yes') {
      screenings.push({
        test: 'Lung Cancer Screening (Low-dose CT)',
        frequency: 'Annually',
        priority: 'High'
      });
    }

    if (screeningData.familyHistory === 'Yes') {
      screenings.push({
        test: 'Prostate Cancer Screening (PSA)',
        frequency: 'Discuss with doctor (age 40+ if family history)',
        priority: 'Medium'
      });
    } else if (age >= 50) {
      screenings.push({
        test: 'Prostate Cancer Screening (PSA)',
        frequency: 'Discuss with doctor',
        priority: 'Medium'
      });
    }

    if (age >= 65) {
      screenings.push({
        test: 'Abdominal Aortic Aneurysm Screening',
        frequency: 'One-time (if you ever smoked)',
        priority: 'Medium'
      });
    }

    setResults({ screenings });
    setView('results');
    toast.success('Screening recommendations ready!');
  };

  // Handle Mental Wellness Submit
  const handleMentalSubmit = (e) => {
    e.preventDefault();
    if (!mentalData.stressLevel || !mentalData.mood) {
      toast.error('Please complete all required fields');
      return;
    }

    const recommendations = [];
    let riskLevel = 'Low';

    if (mentalData.stressLevel === 'High' || mentalData.stressLevel === 'Very High') {
      riskLevel = 'Elevated';
      recommendations.push({
        category: 'Stress Management',
        tip: 'High stress can affect physical and mental health',
        actions: [
          'Practice 10 minutes of meditation daily',
          'Try progressive muscle relaxation',
          'Set boundaries between work and personal time',
          'Consider talking to a therapist'
        ]
      });
    }

    if (mentalData.mood === 'Low' || mentalData.mood === 'Very Low') {
      riskLevel = 'Elevated';
      recommendations.push({
        category: 'Mood Support',
        tip: 'Persistent low mood needs attention',
        actions: [
          'Engage in activities you enjoy',
          'Connect with friends or family',
          'Get 30 minutes of sunlight daily',
          'Consider professional support if this persists'
        ]
      });
    }

    if (mentalData.sleepQuality === 'Poor') {
      recommendations.push({
        category: 'Sleep Quality',
        tip: 'Poor sleep affects mental health',
        actions: [
          'Stick to consistent sleep schedule',
          'Create a relaxing bedtime routine',
          'Limit caffeine after 2 PM',
          'Keep bedroom cool, dark, and quiet'
        ]
      });
    }

    recommendations.push({
      category: 'Daily Practices',
      tip: 'Build mental resilience',
      actions: [
        'Exercise for 30 minutes most days',
        'Practice gratitude journaling',
        'Stay connected with supportive people',
        'Limit alcohol consumption'
      ]
    });

    setResults({ riskLevel, recommendations });
    setView('results');
    toast.success('Mental wellness check complete! 🧠');
  };

  const resetSection = () => {
    setView('input');
    setResults(null);
  };

  const handleSaveReminder = async (reminderData) => {
    try {
      // Auth via httpOnly cookie — no localStorage token needed.
      await axios.post(`${API}/health-reminders`, reminderData, {
        withCredentials: true,
      });
      toast.success('Reminder set! 🔔 Check "My Reminders" to manage it.');
      setShowReminderModal(false);
    } catch (error) {
      console.error('Error saving reminder:', error);
      toast.error('Failed to save reminder');
    }
  };

  const openReminderModal = (suggestion) => {
    setReminderSuggestion(suggestion);
    setShowReminderModal(true);
  };

  // Results View
  if (view === 'results' && results) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 pb-20">
        <div className="max-w-2xl mx-auto px-4 py-6">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <h1 className="text-2xl font-bold text-gray-900 mb-6">
            {activeSection === 'daily' && 'Daily Health Summary'}
            {activeSection === 'cardiovascular' && 'Heart Health Assessment'}
            {activeSection === 'screening' && 'Your Screening Recommendations'}
            {activeSection === 'mental' && 'Mental Wellness Report'}
          </h1>

          <Card className="mb-6 bg-green-50 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-10 w-10 text-green-600" />
                <div>
                  <h3 className="font-bold text-gray-900">Assessment Complete!</h3>
                  <p className="text-sm text-gray-600">Here are your personalized recommendations</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Daily Health Results */}
          {activeSection === 'daily' && results.recommendations && (
            <>
              <div className="space-y-4 mb-6">
                {results.recommendations.map((rec, idx) => (
                  <Card key={idx}>
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-3">
                        <span className="text-3xl">{rec.icon}</span>
                        <div>
                          <h4 className="font-bold text-gray-900 mb-1">{rec.title}</h4>
                          <p className="text-sm text-gray-700 mb-2">{rec.tip}</p>
                          <p className="text-sm text-blue-700 font-medium">{rec.action}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Next Steps</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {results.nextSteps.map((step, idx) => (
                      <p key={idx} className="text-sm text-gray-700 flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        {step}
                      </p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Cardiovascular Results */}
          {activeSection === 'cardiovascular' && (
            <>
              {/* Risk Level Card */}
              <Card className={`mb-6 ${
                results.riskLevel === 'Elevated' ? 'bg-red-50 border-red-300' :
                results.riskLevel === 'Moderate' ? 'bg-amber-50 border-amber-300' :
                'bg-green-50 border-green-300'
              }`}>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Heart className={`h-10 w-10 ${
                      results.riskLevel === 'Elevated' ? 'text-red-600' :
                      results.riskLevel === 'Moderate' ? 'text-amber-600' :
                      'text-green-600'
                    }`} />
                    <div>
                      <h3 className="font-bold text-gray-900 text-lg">
                        Cardiovascular Risk: {results.riskLevel}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {results.riskLevel === 'Elevated' && 'Several risk factors detected - please prioritize heart health'}
                        {results.riskLevel === 'Moderate' && 'Some areas need attention to improve heart health'}
                        {results.riskLevel === 'Low' && 'Great job! Keep maintaining your healthy habits'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Healthy Habits */}
              {results.healthyHabits && results.healthyHabits.length > 0 && (
                <Card className="mb-6 bg-green-50 border-green-200">
                  <CardHeader>
                    <CardTitle className="text-base text-green-800 flex items-center gap-2">
                      <CheckCircle className="h-5 w-5" />
                      Healthy Habits Noticed
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {results.healthyHabits.map((habit, idx) => (
                        <p key={idx} className="text-sm text-gray-700 font-medium">{habit}</p>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Areas of Concern */}
              {results.areasOfConcern && results.areasOfConcern.length > 0 && (
                <Card className="mb-6 bg-amber-50 border-amber-200">
                  <CardHeader>
                    <CardTitle className="text-base text-amber-800 flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Areas That Need Attention
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {results.areasOfConcern.map((area, idx) => (
                        <p key={idx} className="text-sm text-gray-700 font-medium">{area}</p>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Blood Pressure Status */}
              {results.bpStatus && results.bpStatus !== 'Not Provided' && (
                <Card className={`mb-6 ${
                  results.bpStatus === 'High' ? 'bg-red-50 border-red-200' :
                  results.bpStatus === 'Elevated' ? 'bg-amber-50 border-amber-200' :
                  results.bpStatus === 'Normal' ? 'bg-green-50 border-green-200' :
                  'bg-blue-50 border-blue-200'
                }`}>
                  <CardContent className="pt-6">
                    <h3 className="font-bold text-gray-900 mb-2">Blood Pressure Status: {results.bpStatus}</h3>
                  </CardContent>
                </Card>
              )}

              {/* Personalized Recommendations */}
              <h3 className="text-lg font-bold text-gray-900 mb-4">Personalized Recommendations</h3>
              <div className="space-y-4 mb-6">
                {results.recommendations.map((rec, idx) => (
                  <Card key={idx}>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <span className="text-2xl">{rec.icon}</span>
                        {rec.category}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-700 mb-2"><strong>Insight:</strong> {rec.advice}</p>
                      <p className="text-sm text-blue-700 font-medium"><strong>Action:</strong> {rec.action}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}

          {/* Screening Results */}
          {activeSection === 'screening' && results.screenings && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Recommended Screenings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {results.screenings.map((screen, idx) => (
                    <div key={idx} className={`p-4 rounded-lg border-2 ${
                      screen.priority === 'High' ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-bold text-gray-900">{screen.test}</h4>
                        <span className={`text-xs px-2 py-1 rounded ${
                          screen.priority === 'High' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {screen.priority} Priority
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">Frequency: {screen.frequency}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Mental Wellness Results */}
          {activeSection === 'mental' && (
            <>
              <Card className={`mb-6 ${
                results.riskLevel === 'Elevated' ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'
              }`}>
                <CardContent className="pt-6">
                  <h3 className="font-bold text-gray-900 mb-2">Stress Risk Level: {results.riskLevel}</h3>
                  {results.riskLevel === 'Elevated' && (
                    <p className="text-sm text-gray-700">Consider implementing stress management techniques</p>
                  )}
                </CardContent>
              </Card>
              <div className="space-y-4 mb-6">
                {results.recommendations.map((rec, idx) => (
                  <Card key={idx}>
                    <CardHeader>
                      <CardTitle className="text-base">{rec.category}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-700 mb-3">{rec.tip}</p>
                      <div className="space-y-2">
                        {rec.actions.map((action, aidx) => (
                          <p key={aidx} className="text-sm text-gray-700 flex items-start gap-2">
                            <span className="text-blue-600">•</span>
                            {action}
                          </p>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}

          {/* Evidence Badge & Disclaimer */}
          <div className="space-y-3 mb-6">
            <EvidenceBadge className="w-full justify-center" />
            <HealthDisclaimer variant="compact" />
          </div>

          <Card>
            <CardContent className="pt-6 space-y-3">
              <Button 
                className="w-full bg-green-600 hover:bg-green-700" 
                onClick={() => openReminderModal(
                  activeSection === 'cardiovascular' ? 'Next Blood Pressure Check' :
                  activeSection === 'screening' ? 'Schedule Health Screening' :
                  activeSection === 'mental' ? 'Mental Wellness Follow-up' :
                  'Health Check Follow-up'
                )}
              >
                <Bell className="h-4 w-4 mr-2" />
                Set Reminder for Follow-up
              </Button>
              <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={resetSection}>
                Check Again
              </Button>
              <Button className="w-full" variant="outline" onClick={() => navigate('/family-health')}>
                Back to Family Health
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Reminder Modal */}
        <ReminderModal
          isOpen={showReminderModal}
          onClose={() => setShowReminderModal(false)}
          onSave={handleSaveReminder}
          module="men"
          suggestedTitle={reminderSuggestion}
        />
      </div>
    );
  }

  // Input forms for each section
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 pb-20">
      {activeSection === 'hub' && (
        <MensHealthHub onSelectSection={goToSection} />
      )}
      {activeSection !== 'hub' && (
      <div className="max-w-2xl mx-auto px-4 py-6">
        <Button variant="ghost" onClick={goToHub} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Men's Health
        </Button>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            {activeSection === 'daily' && 'Daily Health Tracker v2'}
            {activeSection === 'cardiovascular' && 'Cardiovascular Health Check'}
            {activeSection === 'screening' && 'Preventive Screening Check'}
            {activeSection === 'mental' && 'Mental Wellness Check'}
          </h1>
          <p className="text-gray-600">
            {activeSection === 'daily' && 'Track your daily wellness metrics'}
            {activeSection === 'cardiovascular' && 'Monitor your heart health'}
            {activeSection === 'screening' && 'Get age-appropriate screening recommendations'}
            {activeSection === 'mental' && 'Check your mental health and get support'}
          </p>
        </div>

        {/* Section tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <Button
            variant={activeSection === 'daily' ? 'default' : 'outline'}
            onClick={() => goToSection('daily')}
            size="sm"
          >
            Daily
          </Button>
          <Button
            variant={activeSection === 'cardiovascular' ? 'default' : 'outline'}
            onClick={() => goToSection('cardiovascular')}
            size="sm"
          >
            Heart
          </Button>
          <Button
            variant={activeSection === 'screening' ? 'default' : 'outline'}
            onClick={() => goToSection('screening')}
            size="sm"
          >
            Screening
          </Button>
          <Button
            variant={activeSection === 'mental' ? 'default' : 'outline'}
            onClick={() => goToSection('mental')}
            size="sm"
          >
            Mental
          </Button>
        </div>

        {/* Daily Health Form */}
        {activeSection === 'daily' && (
          <form onSubmit={handleDailySubmit} className="space-y-5">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">How's your stress level today?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {['Low', 'Moderate', 'High', 'Very High'].map(level => (
                    <button
                      key={level}
                      type="button"
                      onClick={() => setDailyData({ ...dailyData, stress: level })}
                      className={`p-3 rounded-lg border-2 font-medium transition-all ${
                        dailyData.stress === level
                          ? 'border-blue-600 bg-blue-100 text-blue-900'
                          : 'border-gray-300 hover:border-blue-300'
                      }`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">What's your energy level?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {['Low', 'Moderate', 'High', 'Very High'].map(level => (
                    <button
                      key={level}
                      type="button"
                      onClick={() => setDailyData({ ...dailyData, energy: level })}
                      className={`p-3 rounded-lg border-2 font-medium transition-all ${
                        dailyData.energy === level
                          ? 'border-blue-600 bg-blue-100 text-blue-900'
                          : 'border-gray-300 hover:border-blue-300'
                      }`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Physical activity today?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {['None', 'Light', 'Moderate', 'Intense'].map(level => (
                    <button
                      key={level}
                      type="button"
                      onClick={() => setDailyData({ ...dailyData, activity: level })}
                      className={`p-3 rounded-lg border-2 font-medium transition-all ${
                        dailyData.activity === level
                          ? 'border-blue-600 bg-blue-100 text-blue-900'
                          : 'border-gray-300 hover:border-blue-300'
                      }`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">How did you sleep last night?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {['Poor', 'Fair', 'Good', 'Excellent'].map(quality => (
                    <button
                      key={quality}
                      type="button"
                      onClick={() => setDailyData({ ...dailyData, sleep: quality })}
                      className={`p-3 rounded-lg border-2 font-medium transition-all ${
                        dailyData.sleep === quality
                          ? 'border-blue-600 bg-blue-100 text-blue-900'
                          : 'border-gray-300 hover:border-blue-300'
                      }`}
                    >
                      {quality}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-lg font-bold">
              Get Today's Insights 💪
            </Button>
          </form>
        )}

        {/* Cardiovascular Health Form */}
        {activeSection === 'cardiovascular' && (
          <form onSubmit={handleCardiovascularSubmit} className="space-y-5">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Blood Pressure (Optional)</CardTitle>
                <p className="text-sm text-gray-600">If you know your current reading</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-gray-600 mb-1 block">Systolic (top number)</label>
                    <Input
                      type="number"
                      min="70"
                      max="200"
                      value={cardiovascularData.systolic}
                      onChange={(e) => setCardiovascularData({ ...cardiovascularData, systolic: e.target.value })}
                      placeholder="120"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 mb-1 block">Diastolic (bottom number)</label>
                    <Input
                      type="number"
                      min="40"
                      max="130"
                      value={cardiovascularData.diastolic}
                      onChange={(e) => setCardiovascularData({ ...cardiovascularData, diastolic: e.target.value })}
                      placeholder="80"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Resting Heart Rate (Optional)</CardTitle>
                <p className="text-sm text-gray-600">Beats per minute (BPM) when at rest</p>
              </CardHeader>
              <CardContent>
                <Input
                  type="number"
                  min="40"
                  max="150"
                  value={cardiovascularData.heartRate}
                  onChange={(e) => setCardiovascularData({ ...cardiovascularData, heartRate: e.target.value })}
                  placeholder="72"
                  className="text-lg"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Activity Level *</CardTitle>
                <p className="text-sm text-gray-600">How active are you on most days?</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {['None', 'Light', 'Moderate', 'High'].map(level => (
                    <button
                      key={level}
                      type="button"
                      onClick={() => setCardiovascularData({ ...cardiovascularData, activityLevel: level })}
                      className={`p-3 rounded-lg border-2 font-medium transition-all ${
                        cardiovascularData.activityLevel === level
                          ? 'border-blue-600 bg-blue-100 text-blue-900'
                          : 'border-gray-300 hover:border-blue-300'
                      }`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Do you smoke? *</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {['Yes', 'No'].map(status => (
                    <button
                      key={status}
                      type="button"
                      onClick={() => setCardiovascularData({ ...cardiovascularData, smokingStatus: status })}
                      className={`p-4 rounded-lg border-2 font-medium transition-all ${
                        cardiovascularData.smokingStatus === status
                          ? 'border-blue-600 bg-blue-100 text-blue-900'
                          : 'border-gray-300 hover:border-blue-300'
                      }`}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Alcohol Intake *</CardTitle>
                <p className="text-sm text-gray-600">Average daily consumption</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {[
                    'None',
                    'Light (< 1 drink/day)',
                    'Moderate (1-2 drinks/day)',
                    'Heavy (3+ drinks/day)'
                  ].map(intake => (
                    <button
                      key={intake}
                      type="button"
                      onClick={() => setCardiovascularData({ ...cardiovascularData, alcoholIntake: intake })}
                      className={`w-full p-3 rounded-lg border-2 font-medium text-left transition-all ${
                        cardiovascularData.alcoholIntake === intake
                          ? 'border-blue-600 bg-blue-100 text-blue-900'
                          : 'border-gray-300 hover:border-blue-300'
                      }`}
                    >
                      {intake}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Stress Level *</CardTitle>
                <p className="text-sm text-gray-600">How stressed do you feel on most days?</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {['Low', 'Moderate', 'High', 'Very High'].map(level => (
                    <button
                      key={level}
                      type="button"
                      onClick={() => setCardiovascularData({ ...cardiovascularData, stressLevel: level })}
                      className={`p-3 rounded-lg border-2 font-medium transition-all ${
                        cardiovascularData.stressLevel === level
                          ? 'border-blue-600 bg-blue-100 text-blue-900'
                          : 'border-gray-300 hover:border-blue-300'
                      }`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Sleep Quality *</CardTitle>
                <p className="text-sm text-gray-600">How well do you typically sleep?</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  {['Poor', 'Fair', 'Good'].map(quality => (
                    <button
                      key={quality}
                      type="button"
                      onClick={() => setCardiovascularData({ ...cardiovascularData, sleepQuality: quality })}
                      className={`p-3 rounded-lg border-2 font-medium transition-all ${
                        cardiovascularData.sleepQuality === quality
                          ? 'border-blue-600 bg-blue-100 text-blue-900'
                          : 'border-gray-300 hover:border-blue-300'
                      }`}
                    >
                      {quality}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Button type="submit" className="w-full bg-red-600 hover:bg-red-700 h-12 text-lg font-bold">
              Get My Heart Health Insight ❤️
            </Button>
          </form>
        )}

        {/* Preventive Screening Form */}
        {activeSection === 'screening' && (
          <form onSubmit={handleScreeningSubmit} className="space-y-5">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">What's your age?</CardTitle>
                <p className="text-sm text-gray-600">We'll recommend age-appropriate screenings</p>
              </CardHeader>
              <CardContent>
                <Input
                  type="number"
                  min="18"
                  max="100"
                  value={screeningData.age}
                  onChange={(e) => setScreeningData({ ...screeningData, age: e.target.value })}
                  className="text-lg font-semibold"
                  placeholder="Enter your age"
                  required
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Family history of cancer? (Optional)</CardTitle>
                <p className="text-sm text-gray-600">Especially prostate, colon, or lung cancer</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {['Yes', 'No'].map(option => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setScreeningData({ ...screeningData, familyHistory: option })}
                      className={`p-4 rounded-lg border-2 font-medium transition-all ${
                        screeningData.familyHistory === option
                          ? 'border-blue-600 bg-blue-100 text-blue-900'
                          : 'border-gray-300 hover:border-blue-300'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Button type="submit" className="w-full bg-green-600 hover:bg-green-700 h-12 text-lg font-bold">
              Check My Screenings 🔍
            </Button>
          </form>
        )}

        {/* Mental Wellness Form */}
        {activeSection === 'mental' && (
          <form onSubmit={handleMentalSubmit} className="space-y-5">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Current stress level?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {['Low', 'Moderate', 'High', 'Very High'].map(level => (
                    <button
                      key={level}
                      type="button"
                      onClick={() => setMentalData({ ...mentalData, stressLevel: level })}
                      className={`p-3 rounded-lg border-2 font-medium transition-all ${
                        mentalData.stressLevel === level
                          ? 'border-purple-600 bg-purple-100 text-purple-900'
                          : 'border-gray-300 hover:border-purple-300'
                      }`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">How's your mood today?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {['Great', 'Good', 'Low', 'Very Low'].map(mood => (
                    <button
                      key={mood}
                      type="button"
                      onClick={() => setMentalData({ ...mentalData, mood })}
                      className={`p-3 rounded-lg border-2 font-medium transition-all ${
                        mentalData.mood === mood
                          ? 'border-purple-600 bg-purple-100 text-purple-900'
                          : 'border-gray-300 hover:border-purple-300'
                      }`}
                    >
                      {mood}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Sleep quality last night?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {['Poor', 'Fair', 'Good', 'Excellent'].map(quality => (
                    <button
                      key={quality}
                      type="button"
                      onClick={() => setMentalData({ ...mentalData, sleepQuality: quality })}
                      className={`p-3 rounded-lg border-2 font-medium transition-all ${
                        mentalData.sleepQuality === quality
                          ? 'border-purple-600 bg-purple-100 text-purple-900'
                          : 'border-gray-300 hover:border-purple-300'
                      }`}
                    >
                      {quality}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700 h-12 text-lg font-bold">
              Check Mental Health 🧠
            </Button>
          </form>
        )}
      </div>
      )}
    </div>
  );
};

export default MensHealth;
